<template>
  <div class="monitoring-strategy-page">
    <el-card class="page-card" shadow="never">
      <div slot="header" class="card-header">
        <div>
          <div class="page-title">监测策略</div>
          <div class="page-description">按设定条件监测广告数据，满足全部条件后自动停用广告。</div>
        </div>
        <el-button type="primary" icon="el-icon-plus" @click="openCreateDialog">新建策略</el-button>
      </div>

      <el-form class="filter-form" :inline="true" :model="query" @submit.native.prevent>
        <el-form-item label="广告主 ID">
          <el-input
            v-model.trim="query.account_id"
            clearable
            placeholder="请输入广告主 ID"
            @keyup.enter.native="searchList"
          />
        </el-form-item>
        <el-form-item label="策略名称">
          <el-input
            v-model.trim="query.pause_strategy_name"
            clearable
            placeholder="请输入策略名称"
            @keyup.enter.native="searchList"
          />
        </el-form-item>
        <el-form-item label="策略编号">
          <el-input
            v-model.trim="query.pause_strategy_no"
            clearable
            placeholder="请输入策略编号"
            @keyup.enter.native="searchList"
          />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="query.pause_strategy_status" style="width: 120px">
            <el-option label="全部" :value="0" />
            <el-option label="正常" :value="1" />
            <el-option label="停用" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="searchList">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetList">重置</el-button>
        </el-form-item>
      </el-form>

      <el-table
        v-loading="loading"
        :data="tableData"
        border
        stripe
        :header-cell-style="{ background: '#f5f7fa', color: '#303133' }"
      >
        <el-table-column prop="pause_strategy_no" label="策略编号" min-width="190">
          <template slot-scope="scope">
            <span class="strategy-no">{{ scope.row.pause_strategy_no || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="pause_strategy_name" label="策略名称" min-width="170" show-overflow-tooltip />
        <el-table-column label="广告主" min-width="260">
          <template slot-scope="scope">
            <div v-if="getAccountDetails(scope.row).length" class="table-account-cell">
              <div class="table-account-primary">
                <div class="table-account-name">{{ getAccountDetails(scope.row)[0].name }}</div>
                <div class="table-account-id">ID：{{ getAccountDetails(scope.row)[0].id }}</div>
              </div>
              <el-popover
                v-if="getAccountDetails(scope.row).length > 1"
                placement="right"
                width="380"
                trigger="click"
                popper-class="monitoring-account-popover"
              >
                <div class="account-popover-title">全部广告主（{{ getAccountDetails(scope.row).length }}）</div>
                <div class="account-popover-list">
                  <div
                    v-for="(account, index) in getAccountDetails(scope.row)"
                    :key="`${account.id}-${index}`"
                    class="account-popover-item"
                  >
                    <div class="account-popover-name">{{ account.name }}</div>
                    <div class="account-popover-id">ID：{{ account.id }}</div>
                  </div>
                </div>
                <el-button slot="reference" type="text" size="mini" class="show-all-accounts">
                  另有 {{ getAccountDetails(scope.row).length - 1 }} 个，查看全部
                </el-button>
              </el-popover>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="监测条件（全部满足）" min-width="300">
          <template slot-scope="scope">
            <div v-if="getRowConditions(scope.row).length" class="condition-list">
              <div v-for="(condition, index) in getRowConditions(scope.row)" :key="index" class="condition-item">
                <span class="condition-index">{{ index + 1 }}</span>
                {{ getConditionText(condition) }}
              </div>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="110" align="center">
          <template slot-scope="scope">
            <div class="status-cell">
              <el-switch
                :value="normalizeStatus(scope.row.pause_strategy_status === undefined ? scope.row.status : scope.row.pause_strategy_status)"
                :active-value="1"
                :inactive-value="2"
                active-color="#13ce66"
                @change="changeStatus(scope.row, $event)"
              />
              <span>{{ scope.row.pause_strategy_status_text || getStatusText(scope.row.pause_strategy_status) }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="created_at_text" label="创建时间" min-width="165">
          <template slot-scope="scope">
            {{ scope.row.created_at_text || scope.row.created_at || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="pause_last_at_text" label="最近监测时间" min-width="165">
          <template slot-scope="scope">
            {{ scope.row.pause_last_at_text || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="pause_last_remark" label="备注" min-width="180" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.pause_last_remark || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="210" fixed="right" align="center">
          <template slot-scope="scope">
            <el-button type="text" @click="openEditDialog(scope.row)">编辑</el-button>
            <el-button type="text" @click="openLogDialog(scope.row)">关停记录</el-button>
            <el-button type="text" class="danger-button" @click="removeStrategy(scope.row)">删除</el-button>
          </template>
        </el-table-column>
        <template slot="empty">
          <div class="empty-state">
            <i class="el-icon-document" />
            <div>暂无监测策略</div>
            <el-button type="text" @click="openCreateDialog">立即新建</el-button>
          </div>
        </template>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          background
          :current-page="query.page"
          :page-size="query.page_size"
          :page-sizes="[10, 20, 30, 50]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <el-dialog
      :title="editing ? '编辑监测策略' : '新建监测策略'"
      :visible.sync="strategyDialogVisible"
      width="720px"
      :close-on-click-modal="false"
      @closed="resetStrategyForm"
    >
      <el-form ref="strategyForm" :model="form" :rules="formRules" label-width="105px">
        <el-form-item label="策略名称" prop="pause_strategy_name">
          <el-input
            v-model.trim="form.pause_strategy_name"
            maxlength="50"
            show-word-limit
            placeholder="请输入策略名称"
          />
        </el-form-item>
        <el-form-item label="广告主" prop="account_ids">
          <div class="account-selector">
            <div class="account-selector-header">
              <span :class="advertiserOptions.length ? 'account-summary' : 'account-placeholder'">
                {{ advertiserOptions.length ? `已选择 ${advertiserOptions.length} 个广告主` : '暂未选择广告主' }}
              </span>
              <div class="account-selector-actions">
                <el-button
                  v-if="advertiserOptions.length"
                  type="text"
                  class="clear-account-button"
                  @click="clearSelectedAccounts"
                >清空已选</el-button>
                <el-button
                  class="choose-account-button"
                  icon="el-icon-user"
                  @click="openAccountDialog"
                >{{ advertiserOptions.length ? '重新选择' : '选择广告主' }}</el-button>
              </div>
            </div>
            <div v-if="advertiserOptions.length" class="selected-account-list">
              <el-tag
                v-for="account in advertiserOptions"
                :key="account.account_id"
                closable
                @close="removeSelectedAccount(account.account_id)"
              >
                <span class="account-tag-text" :title="formatAccountOption(account)">{{ formatAccountOption(account) }}</span>
              </el-tag>
            </div>
          </div>
          <div class="form-tip">支持按账号 ID、账号名称、账号类型和账号状态筛选，可跨页多选。</div>
        </el-form-item>

        <el-form-item label="监测条件" required>
          <div class="conditions-box">
            <div class="conditions-header">
              <span>需同时满足以下全部条件</span>
              <el-button type="text" icon="el-icon-plus" @click="addCondition">添加条件</el-button>
            </div>
            <div v-if="!form.pause_strategy_where.length" class="condition-empty">请至少添加一个监测条件</div>
            <div
              v-for="(condition, index) in form.pause_strategy_where"
              :key="condition.rowKey"
              class="condition-row"
            >
              <span class="condition-sequence">{{ index + 1 }}</span>
              <el-select
                v-model="condition.field"
                class="condition-field"
                placeholder="选择指标"
                @change="handleConditionFieldChange(condition)"
              >
                <el-option
                  v-for="option in conditionOptions"
                  :key="option.field"
                  :label="option.field_name"
                  :value="option.field"
                  :disabled="isConditionDisabled(option, condition)"
                />
              </el-select>
              <el-select v-model="condition.operator" class="condition-operator" placeholder="关系">
                <el-option
                  v-for="operator in getOperators(condition.field)"
                  :key="operator"
                  :label="operator"
                  :value="operator"
                />
              </el-select>
              <el-input-number
                v-model="condition.value"
                class="condition-value"
                :controls="false"
                :precision="getConditionPrecision(condition.field)"
                placeholder="条件值"
              />
              <el-button
                type="text"
                icon="el-icon-delete"
                class="danger-button remove-condition"
                @click="removeCondition(index)"
              />
            </div>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="strategyDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitStrategy">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="选择广告主"
      :visible.sync="accountDialogVisible"
      width="900px"
      append-to-body
      :close-on-click-modal="false"
      @closed="resetAccountDialog"
    >
      <el-form class="account-filter-form" :inline="true" :model="accountQuery" @submit.native.prevent>
        <el-form-item label="账号 ID">
          <el-input
            :value="accountIdSummary"
            readonly
            placeholder="点击批量输入账号 ID"
            class="batch-account-id-input"
            @click.native="openAccountIdInput"
          >
            <i slot="suffix" class="el-input__icon el-icon-edit-outline" />
          </el-input>
        </el-form-item>
        <el-form-item label="账号名称">
          <el-input
            v-model.trim="accountQuery.account_name"
            clearable
            placeholder="请输入账号名称"
            @keyup.enter.native="searchAccountList"
          />
        </el-form-item>
        <el-form-item label="账号类型">
          <el-select v-model="accountQuery.type" clearable placeholder="请选择" style="width: 150px">
            <el-option
              v-for="option in accountTypeOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="账号状态">
          <el-select v-model="accountQuery.account_status" clearable placeholder="请选择" style="width: 170px">
            <el-option-group
              v-for="group in accountStatusOptions"
              :key="group.label"
              :label="group.label"
            >
              <el-option
                v-for="option in group.options"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-option-group>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="searchAccountList">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetAccountSearch">重置</el-button>
        </el-form-item>
      </el-form>

      <div class="account-selection-summary">已选择 {{ selectedAccountCount }} 个广告主</div>
      <el-table
        ref="accountTable"
        v-loading="accountLoading"
        :data="accountTableData"
        border
        stripe
        row-key="account_id"
        @select="handleAccountSelect"
        @select-all="handleAccountSelectAll"
      >
        <el-table-column type="selection" width="50" :reserve-selection="true" />
        <el-table-column prop="account_name" label="广告主名称" min-width="190" show-overflow-tooltip />
        <el-table-column prop="account_id" label="账号 ID" min-width="130" />
        <el-table-column prop="type_text" label="账号类型" min-width="120">
          <template slot-scope="scope">{{ scope.row.type_text || getAccountTypeText(scope.row.type) }}</template>
        </el-table-column>
        <el-table-column prop="account_status_text" label="账号状态" min-width="150" />
        <el-table-column prop="service_name" label="服务商" min-width="130" show-overflow-tooltip />
      </el-table>
      <div class="pagination-wrap">
        <el-pagination
          background
          :current-page="accountQuery.page"
          :page-size="accountQuery.page_size"
          :page-sizes="[10, 20, 30, 50]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="accountTotal"
          @size-change="handleAccountSizeChange"
          @current-change="handleAccountPageChange"
        />
      </div>
      <span slot="footer">
        <el-button @click="accountDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmAccountSelection">确定选择</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="批量输入账号 ID"
      :visible.sync="accountIdInputVisible"
      width="480px"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-input
        v-model="accountIdText"
        type="textarea"
        :rows="10"
        resize="vertical"
        :placeholder="accountIdPlaceholder"
      />
      <div class="batch-input-tip">
        支持换行或空格分隔多个账号 ID，例如：72952434 72952433 73165621。
      </div>
      <span slot="footer">
        <el-button @click="accountIdInputVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmAccountIds">确定</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="策略关停记录"
      :visible.sync="logDialogVisible"
      width="850px"
      :close-on-click-modal="false"
    >
      <div class="log-toolbar">
        <div>
          <span class="log-strategy-name">{{ currentStrategy.pause_strategy_name }}</span>
          <span class="log-strategy-no">{{ currentStrategy.pause_strategy_no }}</span>
        </div>
        <div>
          <el-date-picker
            v-model="logDateRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :clearable="false"
          />
          <el-button type="primary" icon="el-icon-search" @click="searchLogs">查询</el-button>
        </div>
      </div>
      <el-table v-loading="logLoading" :data="logData" border stripe>
        <el-table-column prop="created_at_text" label="执行时间" min-width="165">
          <template slot-scope="scope">{{ scope.row.created_at_text || scope.row.created_at || '-' }}</template>
        </el-table-column>
        <el-table-column label="广告主" min-width="180">
          <template slot-scope="scope">
            <div>{{ scope.row.account_name || '-' }}</div>
            <div v-if="scope.row.account_id" class="secondary-text">ID：{{ scope.row.account_id }}</div>
          </template>
        </el-table-column>
        <el-table-column label="停用广告" min-width="260">
          <template slot-scope="scope">
            <div v-if="getStoppedAdDetails(scope.row).length">
              <div
                v-for="(advert, index) in getStoppedAdDetails(scope.row)"
                :key="advert.id || index"
                class="adgroup-item"
              >
                {{ formatLogNameAndId(advert.name, advert.id) }}
              </div>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="report_results" label="执行结果" min-width="160">
          <template slot-scope="scope">{{ formatReportResult(scope.row.report_results) }}</template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrap">
        <el-pagination
          background
          :current-page="logQuery.page"
          :page-size="logQuery.page_size"
          layout="total, prev, pager, next"
          :total="logTotal"
          @current-change="handleLogPageChange"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { advertiserList } from '@/api/customer'
import {
  createPauseStrategy,
  deletePauseStrategy,
  getPauseStrategyConditions,
  getPauseStrategyList,
  getPauseStrategyLogs,
  updatePauseStrategy,
  updatePauseStrategyStatus
} from '@/api/pauseStrategy'

function getDateByOffset(offset = 0) {
  const date = new Date()
  date.setDate(date.getDate() + offset)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

function createConditionRow(source = {}) {
  return {
    rowKey: `${Date.now()}-${Math.random()}`,
    field: source.field || '',
    operator: source.operator || '',
    value: source.value === undefined || source.value === null ? undefined : Number(source.value)
  }
}

export default {
  name: 'TencentMonitoringStrategy',
  data() {
    const validateAccounts = (rule, value, callback) => {
      if (!value || !value.length) {
        callback(new Error('请至少选择一个广告主'))
        return
      }
      const invalid = value.some(item => !/^\d+$/.test(String(item)))
      invalid ? callback(new Error('广告主 ID 只能为数字')) : callback()
    }
    return {
      loading: false,
      query: {
        page: 1,
        page_size: 10,
        account_id: '',
        pause_strategy_name: '',
        pause_strategy_no: '',
        pause_strategy_status: 0
      },
      tableData: [],
      total: 0,
      conditionOptions: [],
      strategyDialogVisible: false,
      editing: false,
      submitting: false,
      accountDialogVisible: false,
      accountIdInputVisible: false,
      accountIdText: '',
      accountIdPlaceholder: '支持换行或空格分隔，例如：\n90605855\n80255765\n80346299\n\n也可以输入：90605855 80255765 80346299',
      accountLoading: false,
      accountTableData: [],
      accountTotal: 0,
      accountSelectionMap: {},
      accountQuery: {
        account_id: '',
        account_name: '',
        type: null,
        account_status: null,
        page: 1,
        page_size: 10
      },
      accountTypeOptions: [
        { value: 1, label: '微信公众号' },
        { value: 2, label: '腾讯广告(adq)' }
      ],
      accountStatusOptions: [
        {
          label: '腾讯',
          options: [
            { value: 'CUSTOMER_STATUS_NORMAL', label: '有效' },
            { value: 'CUSTOMER_STATUS_PENDING', label: '待审核' },
            { value: 'CUSTOMER_STATUS_DENIED', label: '审核不通过' },
            { value: 'CUSTOMER_STATUS_FROZEN', label: '封停' },
            { value: 'CUSTOMER_STATUS_FROZEN_TEMPORARILY', label: '临时冻结' }
          ]
        },
        {
          label: '微信',
          options: [
            { value: 'STATUS_PRE_AUTHORIZATION', label: '预授权' },
            { value: 'STATUS_CONFIRM_AUTHORIZATION', label: '确认授权/已绑定' },
            { value: 'STATUS_REQUEST_CANCELATION', label: '请求撤销授权' }
          ]
        }
      ],
      advertiserOptions: [],
      form: this.getEmptyForm(),
      formRules: {
        pause_strategy_name: [
          { required: true, message: '请输入策略名称', trigger: 'blur' },
          { max: 50, message: '策略名称不能超过 50 个字符', trigger: 'blur' }
        ],
        account_ids: [
          { required: true, validator: validateAccounts, trigger: 'change' }
        ]
      },
      logDialogVisible: false,
      logLoading: false,
      currentStrategy: {},
      logData: [],
      logTotal: 0,
      logDateRange: [getDateByOffset(-7), getDateByOffset()],
      logQuery: {
        page: 1,
        page_size: 10
      }
    }
  },
  computed: {
    selectedAccountCount() {
      return Object.keys(this.accountSelectionMap).length
    },
    accountIdSummary() {
      const ids = String(this.accountQuery.account_id || '').split(/\s+/).filter(Boolean)
      if (!ids.length) return ''
      return ids.length === 1 ? ids[0] : `已输入 ${ids.length} 个账号 ID`
    }
  },
  created() {
    this.fetchList()
    this.fetchConditionOptions()
  },
  methods: {
    getEmptyForm() {
      return {
        pause_strategy_no: '',
        pause_strategy_name: '',
        account_ids: [],
        pause_strategy_where: []
      }
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await getPauseStrategyList(this.query)
        const data = res.data || {}
        this.tableData = Array.isArray(data) ? data : (data.list || [])
        this.total = Array.isArray(data) ? data.length : Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    async fetchConditionOptions() {
      const res = await getPauseStrategyConditions()
      this.conditionOptions = Array.isArray(res.data) ? res.data : []
    },
    handleSizeChange(size) {
      this.query.page = 1
      this.query.page_size = size
      this.fetchList()
    },
    handleCurrentChange(page) {
      this.query.page = page
      this.fetchList()
    },
    searchList() {
      this.query.page = 1
      this.fetchList()
    },
    resetList() {
      this.query = {
        page: 1,
        page_size: this.query.page_size,
        account_id: '',
        pause_strategy_name: '',
        pause_strategy_no: '',
        pause_strategy_status: 0
      }
      this.fetchList()
    },
    openCreateDialog() {
      this.editing = false
      this.form = this.getEmptyForm()
      this.advertiserOptions = []
      this.addCondition()
      this.strategyDialogVisible = true
    },
    openEditDialog(row) {
      this.editing = true
      const accountIds = this.getAccountIds(row)
      this.form = {
        pause_strategy_no: row.pause_strategy_no,
        pause_strategy_name: row.pause_strategy_name || '',
        account_ids: accountIds.map(item => String(item)),
        pause_strategy_where: this.getRowConditions(row).map(item => createConditionRow(item))
      }
      this.advertiserOptions = accountIds.map((id, index) => ({
        account_id: id,
        account_name: this.getAccountNames(row)[index] || ''
      }))
      if (!this.form.pause_strategy_where.length) this.addCondition()
      this.strategyDialogVisible = true
    },
    resetStrategyForm() {
      this.form = this.getEmptyForm()
      this.editing = false
      if (this.$refs.strategyForm) this.$refs.strategyForm.clearValidate()
    },
    openAccountDialog() {
      this.accountSelectionMap = {}
      this.advertiserOptions.forEach(account => {
        this.$set(this.accountSelectionMap, String(account.account_id), account)
      })
      this.accountDialogVisible = true
      this.fetchAccountList()
    },
    openAccountIdInput() {
      this.accountIdText = String(this.accountQuery.account_id || '')
        .split(/\s+/)
        .filter(Boolean)
        .join('\n')
      this.accountIdInputVisible = true
    },
    confirmAccountIds() {
      const ids = this.accountIdText
        .split(/\s+/)
        .map(item => item.trim())
        .filter(Boolean)
      const invalidIds = ids.filter(item => !/^\d+$/.test(item))
      if (invalidIds.length) {
        this.$message.warning(`账号 ID 只能为数字，请检查：${invalidIds.slice(0, 3).join('、')}`)
        return
      }
      const uniqueIds = Array.from(new Set(ids))
      this.accountQuery.account_id = uniqueIds.join(' ')
      this.accountIdText = uniqueIds.join('\n')
      this.accountIdInputVisible = false
    },
    async fetchAccountList() {
      this.accountLoading = true
      try {
        const res = await advertiserList(this.accountQuery)
        const data = res.data || {}
        this.accountTableData = data.list || []
        this.accountTotal = Number(data.total || 0)
        this.$nextTick(() => {
          this.accountTableData.forEach(row => {
            this.$refs.accountTable.toggleRowSelection(
              row,
              Boolean(this.accountSelectionMap[String(row.account_id)])
            )
          })
        })
      } finally {
        this.accountLoading = false
      }
    },
    searchAccountList() {
      this.accountQuery.page = 1
      this.fetchAccountList()
    },
    resetAccountSearch() {
      this.accountQuery = {
        account_id: '',
        account_name: '',
        type: null,
        account_status: null,
        page: 1,
        page_size: this.accountQuery.page_size
      }
      this.fetchAccountList()
    },
    handleAccountSizeChange(size) {
      this.accountQuery.page = 1
      this.accountQuery.page_size = size
      this.fetchAccountList()
    },
    handleAccountPageChange(page) {
      this.accountQuery.page = page
      this.fetchAccountList()
    },
    handleAccountSelect(selection, row) {
      const selected = selection.some(item => String(item.account_id) === String(row.account_id))
      if (selected) {
        this.$set(this.accountSelectionMap, String(row.account_id), row)
      } else {
        this.$delete(this.accountSelectionMap, String(row.account_id))
      }
    },
    handleAccountSelectAll(selection) {
      this.accountTableData.forEach(row => {
        this.$delete(this.accountSelectionMap, String(row.account_id))
      })
      selection.forEach(row => {
        this.$set(this.accountSelectionMap, String(row.account_id), row)
      })
    },
    confirmAccountSelection() {
      this.advertiserOptions = Object.keys(this.accountSelectionMap).map(key => this.accountSelectionMap[key])
      this.form.account_ids = this.advertiserOptions.map(item => String(item.account_id))
      this.accountDialogVisible = false
      this.$nextTick(() => {
        if (this.$refs.strategyForm) this.$refs.strategyForm.validateField('account_ids')
      })
    },
    resetAccountDialog() {
      this.accountTableData = []
      this.accountTotal = 0
      this.accountSelectionMap = {}
      this.accountIdInputVisible = false
      this.accountIdText = ''
      this.accountQuery = {
        account_id: '',
        account_name: '',
        type: null,
        account_status: null,
        page: 1,
        page_size: 10
      }
    },
    removeSelectedAccount(accountId) {
      const id = String(accountId)
      this.advertiserOptions = this.advertiserOptions.filter(item => String(item.account_id) !== id)
      this.form.account_ids = this.form.account_ids.filter(item => String(item) !== id)
    },
    clearSelectedAccounts() {
      this.advertiserOptions = []
      this.form.account_ids = []
      this.$nextTick(() => {
        if (this.$refs.strategyForm) this.$refs.strategyForm.validateField('account_ids')
      })
    },
    formatAccountOption(account) {
      const accountName = account.account_name || account.advertiser_name
      return accountName
        ? `${accountName}（${account.account_id}）`
        : String(account.account_id)
    },
    getAccountTypeText(type) {
      const option = this.accountTypeOptions.find(item => Number(item.value) === Number(type))
      return option ? option.label : '-'
    },
    addCondition() {
      this.form.pause_strategy_where.push(createConditionRow())
    },
    removeCondition(index) {
      this.form.pause_strategy_where.splice(index, 1)
    },
    handleConditionFieldChange(condition) {
      const operators = this.getOperators(condition.field)
      condition.operator = operators[0] || ''
      condition.value = undefined
    },
    getConditionOption(field) {
      return this.conditionOptions.find(item => item.field === field) || {}
    },
    getOperators(field) {
      const option = this.getConditionOption(field)
      return option.operator || []
    },
    getConditionPrecision(field) {
      const fieldType = this.getConditionOption(field).field_type
      return fieldType === 'integer' || fieldType === 'int' ? 0 : undefined
    },
    isConditionDisabled(option, currentCondition) {
      if (option.multiple_choice) return false
      return this.form.pause_strategy_where.some(item => item !== currentCondition && item.field === option.field)
    },
    validateConditions() {
      if (!this.form.pause_strategy_where.length) {
        this.$message.warning('请至少添加一个监测条件')
        return false
      }
      const incomplete = this.form.pause_strategy_where.some(item => {
        return !item.field || !item.operator || item.value === undefined || item.value === null
      })
      if (incomplete) {
        this.$message.warning('请完整填写每一项监测条件')
        return false
      }
      return true
    },
    submitStrategy() {
      this.$refs.strategyForm.validate(async valid => {
        if (!valid || !this.validateConditions()) return
        this.submitting = true
        try {
          const payload = {
            account_ids: this.form.account_ids.map(id => Number(id)),
            pause_strategy_name: this.form.pause_strategy_name,
            pause_strategy_where: this.form.pause_strategy_where.map(item => ({
              field: item.field,
              value: item.value,
              operator: item.operator
            }))
          }
          if (this.editing) {
            payload.pause_strategy_no = this.form.pause_strategy_no
            await updatePauseStrategy(payload)
          } else {
            await createPauseStrategy(payload)
          }
          this.$message.success(this.editing ? '策略更新成功' : '策略创建成功')
          this.strategyDialogVisible = false
          this.fetchList()
        } finally {
          this.submitting = false
        }
      })
    },
    async changeStatus(row, status) {
      try {
        await updatePauseStrategyStatus({
          pause_strategy_status: status,
          pause_strategy_no: row.pause_strategy_no
        })
        this.$set(row, 'pause_strategy_status', status)
        this.$message.success(status === 1 ? '策略已恢复正常' : '策略已停用')
      } catch (error) {
        this.fetchList()
      }
    },
    removeStrategy(row) {
      this.$confirm(`确定删除策略“${row.pause_strategy_name || row.pause_strategy_no}”吗？`, '删除确认', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async() => {
        await deletePauseStrategy({ pause_strategy_no: row.pause_strategy_no })
        this.$message.success('删除成功')
        if (this.tableData.length === 1 && this.query.page > 1) this.query.page -= 1
        this.fetchList()
      }).catch(() => {})
    },
    openLogDialog(row) {
      this.currentStrategy = row
      this.logQuery.page = 1
      this.logDateRange = [getDateByOffset(-7), getDateByOffset()]
      this.logDialogVisible = true
      this.fetchLogs()
    },
    searchLogs() {
      this.logQuery.page = 1
      this.fetchLogs()
    },
    handleLogPageChange(page) {
      this.logQuery.page = page
      this.fetchLogs()
    },
    async fetchLogs() {
      this.logLoading = true
      try {
        const res = await getPauseStrategyLogs({
          page: this.logQuery.page,
          page_size: this.logQuery.page_size,
          pause_strategy_no: this.currentStrategy.pause_strategy_no,
          start_date: this.logDateRange[0],
          end_date: this.logDateRange[1]
        })
        const data = res.data || {}
        this.logData = data.list || []
        this.logTotal = Number(data.total || 0)
      } finally {
        this.logLoading = false
      }
    },
    getRowConditions(row) {
      const value = row.pause_strategy_where || row.strategy_where || row.where || []
      if (Array.isArray(value)) return value
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value)
          return Array.isArray(parsed) ? parsed : []
        } catch (error) {
          return []
        }
      }
      return []
    },
    getConditionText(condition) {
      const option = this.getConditionOption(condition.field)
      return `${condition.field_name || option.field_name || condition.field || '-'} ${condition.operator || ''} ${condition.value}`
    },
    getAccountIds(row) {
      const ids = row.account_ids || row.advertiser_ids || row.account_id || []
      if (Array.isArray(ids)) {
        return ids.map(item => {
          return typeof item === 'object' ? (item.account_id || item.advertiser_id || item.id) : item
        }).filter(item => item !== undefined && item !== null && item !== '')
      }
      if (ids === null || ids === undefined || ids === '') return []
      return String(ids).split(',').map(item => item.trim()).filter(Boolean)
    },
    getAccountNames(row) {
      const names = row.account_names || row.advertiser_names || row.account_name || []
      if (Array.isArray(names)) {
        return names.map(item => {
          if (typeof item !== 'object') return item
          return item.advertiser_name || item.account_name || item.name || ''
        })
      }
      if (!names) return []
      return String(names).split(',').map(item => item.trim()).filter(Boolean)
    },
    getAccountDetails(row) {
      if (Array.isArray(row.account_names) && row.account_names.length) {
        const accountIds = this.getAccountIds(row)
        return row.account_names.map((item, index) => {
          if (typeof item !== 'object') {
            return {
              id: accountIds[index] || '-',
              name: String(item)
            }
          }
          const id = item.account_id || item.advertiser_id || item.id || accountIds[index]
          const name = item.advertiser_name || item.account_name || item.name
          return {
            id: id || '-',
            name: name || '-'
          }
        })
      }
      const ids = this.getAccountIds(row)
      const names = this.getAccountNames(row)
      return ids.map((id, index) => ({
        id: id,
        name: names[index] || '-'
      }))
    },
    getStatusText(status) {
      return Number(status) === 1 ? '正常' : '停用'
    },
    normalizeStatus(status) {
      return Number(status) === 1 ? 1 : 2
    },
    getStoppedAdDetails(row) {
      const toArray = value => {
        if (Array.isArray(value)) return value
        return value === null || value === undefined || value === '' ? [] : [value]
      }
      const adgroupNames = toArray(row.adgroup_name || row.adgroup_names || row.adgroup)
      const adgroupIds = toArray(row.adgroup_id || row.adgroup_ids)
      const count = Math.max(adgroupNames.length, adgroupIds.length)
      return Array.from({ length: count }, (item, index) => ({
        name: adgroupNames[index] || '',
        id: adgroupIds[index] || ''
      }))
    },
    formatLogNameAndId(name, id) {
      if (name && id) return `${name}（${id}）`
      return String(name || id || '-')
    },
    formatReportResult(result) {
      if (result === null || result === undefined || result === '') return '-'
      if (typeof result === 'string') return result
      try {
        return JSON.stringify(result)
      } catch (error) {
        return String(result)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.monitoring-strategy-page {
  min-height: calc(100vh - 84px);
  padding: 24px;
  background: #f3f5f8;
}

.page-card {
  border: 0;
  border-radius: 8px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  color: #303133;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.page-description,
.form-tip,
.secondary-text,
.log-strategy-no {
  color: #909399;
  font-size: 12px;
}

.page-description {
  margin-top: 3px;
}

.filter-form {
  margin-bottom: 4px;
  padding: 16px 16px 0;
  background: #f8f9fb;
  border-radius: 6px;
}

.filter-form .el-input {
  width: 180px;
}

.strategy-no {
  color: #606266;
  font-family: Consolas, Monaco, monospace;
}

.status-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #606266;
  font-size: 12px;
  line-height: 22px;
}

.table-account-cell {
  padding: 5px 0;
}

.table-account-primary {
  padding-left: 9px;
  border-left: 3px solid #409eff;
}

.table-account-name {
  color: #303133;
  line-height: 20px;
  overflow-wrap: anywhere;
}

.table-account-id {
  margin-top: 2px;
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}

.show-all-accounts {
  padding: 4px 0 0;
  color: #409eff;
}

.condition-list {
  padding: 4px 0;
}

.condition-item {
  margin: 4px 0;
  color: #606266;
}

.condition-index,
.condition-sequence {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 7px;
  color: #409eff;
  font-size: 12px;
  background: #ecf5ff;
  border-radius: 50%;
}

.danger-button {
  color: #f56c6c;
}

.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  padding-top: 20px;
}

.empty-state {
  padding: 28px 0;
  color: #909399;
  line-height: 28px;
}

.empty-state i {
  font-size: 36px;
}

.full-width {
  width: 100%;
}

.account-selector {
  width: 100%;
  min-height: 40px;
  padding: 8px 10px 10px;
  box-sizing: border-box;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.account-selector-header {
  display: flex;
  min-height: 32px;
  align-items: center;
  justify-content: space-between;
}

.account-summary {
  color: #606266;
  font-size: 13px;
}

.selected-account-list {
  display: flex;
  max-height: 132px;
  padding-top: 8px;
  flex-wrap: wrap;
  gap: 6px;
  overflow-y: auto;
  border-top: 1px solid #ebeef5;
}

.selected-account-list .el-tag {
  display: inline-flex;
  max-width: calc(50% - 3px);
  align-items: center;
}

.account-tag-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-placeholder {
  color: #c0c4cc;
}

.account-selector-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  margin-left: 16px;
}

.clear-account-button {
  margin-right: 4px;
  color: #f56c6c;
}

.choose-account-button {
  color: #606266;
  background: #f5f7fa;
  border-color: #dcdfe6;
}

.choose-account-button:hover,
.choose-account-button:focus {
  color: #409eff;
  background: #ecf5ff;
  border-color: #b3d8ff;
}

.account-filter-form {
  padding: 16px 16px 0;
  background: #f8f9fb;
  border-radius: 6px;
}

.account-filter-form .el-input {
  width: 160px;
}

.batch-account-id-input {
  cursor: pointer;
}

.batch-account-id-input .el-input__suffix {
  color: #909399;
}

.batch-input-tip {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
  line-height: 20px;
}

.account-selection-summary {
  margin: 14px 0 10px;
  color: #409eff;
  font-size: 13px;
}

.form-tip {
  margin-top: 4px;
  line-height: 20px;
}

.conditions-box {
  padding: 12px;
  background: #f8f9fb;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

.conditions-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  color: #606266;
}

.condition-empty {
  padding: 18px;
  color: #909399;
  text-align: center;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
}

.condition-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.condition-row:last-child {
  margin-bottom: 0;
}

.condition-sequence {
  flex: 0 0 20px;
}

.condition-field {
  width: 245px;
}

.condition-operator {
  width: 90px;
  margin-left: 8px;
}

.condition-value {
  width: 170px;
  margin-left: 8px;
}

.remove-condition {
  margin-left: 8px;
}

.log-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.log-toolbar .el-button {
  margin-left: 8px;
}

.log-strategy-name {
  display: block;
  margin-bottom: 4px;
  color: #303133;
  font-weight: 600;
}

.adgroup-item {
  margin: 4px 0;
}

.account-popover-title {
  margin-bottom: 8px;
  color: #303133;
  font-weight: 600;
}

.account-popover-list {
  max-height: 280px;
  overflow-y: auto;
}

.account-popover-item {
  padding: 8px 4px;
  border-bottom: 1px solid #ebeef5;
}

.account-popover-item:last-child {
  border-bottom: 0;
}

.account-popover-name {
  color: #303133;
  line-height: 20px;
  overflow-wrap: anywhere;
}

.account-popover-id {
  margin-top: 2px;
  color: #909399;
  font-size: 12px;
}
</style>
